// Lib
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { waitUntil } from '../../../common/utils/functionUtils';
import globalLogger from '../../logger/logger';
import { isPlatformIframe } from '../../platform/utils/platformDetailsUtils';
import getClientConfig from '../../utils/getClientConfig';

// Selectors
import { getPlatformDetailsSelector } from '../../platform/platformSelector';

// Constants
import { OneTrustStatus } from './oneTrustTypes';

const logger = globalLogger.createChannel('one-trust-loader');

const oneTrustLoaded = () => !!window.OneTrust && !!window.OneTrust.Init && !!window.OneTrust.GetDomainData;

const useOneTrustLoader = ({
    isGuest,
    userId,
    userOTToken,
}: {
    isGuest: boolean;
    userId: string;
    userOTToken: string;
}): [OneTrustStatus] => {
    const config = getClientConfig();

    const platformDetails = useSelector(getPlatformDetailsSelector);
    const [oneTrustStatus, setOneTrustStatus] = useState<OneTrustStatus>(OneTrustStatus.UNFETCHED);

    const loadOneTrust = useCallback(
        (userId?: string, userOTToken?: string) => {
            // If user authenticated, set user information in window.OneTrust.dataSubjectParams before loading OneTrust
            // - This will be used to get user's cookie preference from OneTrust cloud, or send user's cookie preference
            //   to OneTrust Cloud
            // - This will help in Safari, where user's local cookie pref is removed by Safari every 7 days due to ITP

            window.OneTrust = window.OneTrust || {};
            window.OneTrust.dataSubjectParams = {};

            if (userId && userOTToken) {
                window.OneTrust.dataSubjectParams = {
                    id: userId,
                    token: userOTToken,
                    isAnonymous: false,
                };
            }

            // If OneTrust has not been loaded yet, embed OneTrust script into the DOM
            if (oneTrustStatus === OneTrustStatus.UNFETCHED) {
                const { domainScript } = config.oneTrust!;

                const script = document.createElement('script');
                script.src = `https://cdn.cookielaw.org/consent/${domainScript}/otSDKStub.js`;
                script.dataset.domainScript = domainScript;

                document.body.appendChild(script);

                setOneTrustStatus(OneTrustStatus.FETCHING);

                waitUntil(oneTrustLoaded, { duration: 60000 })
                    .then(() => {
                        setOneTrustStatus(OneTrustStatus.INITIALISED);
                    })
                    .catch((error) => {
                        setOneTrustStatus(OneTrustStatus.ERROR);
                        logger.error('Failed to load OneTrust', error);
                    });

                return;
            }

            // If OneTrust has previously been loaded, just sync the user cookie profile from OneTrust cloud
            if (oneTrustStatus === OneTrustStatus.INITIALISED) {
                window.OneTrust.syncConsentProfile?.(userId, userOTToken, false);
            }
        },
        [oneTrustStatus],
    );

    useEffect(() => {
        // If in iframe, don't show cookie banner, and use default cookie settings per region
        if (isPlatformIframe(platformDetails)) return;

        if (!isGuest && userId && userId !== 'guest') {
            loadOneTrust(userId, userOTToken);
        }

        if (isGuest) {
            loadOneTrust();
        }
    }, [isGuest, userId]);

    return [oneTrustStatus];
};

export default useOneTrustLoader;
